/* Native suggestions plus live, published packs and conservative spelling matches. */
(() => {
  const aliases = Object.freeze({
    swit: 'sweet', suit: 'sweet', swet: 'sweet', suet: 'sweet',
    blak: 'black', blac: 'black',
    dansin: 'dancing', dansing: 'dancing', dancin: 'dancing',
    nait: 'night', naigt: 'night', nigth: 'night',
    winer: 'winner', winerr: 'winner',
    intese: 'intense',
    mitica: 'mithyka', mitika: 'mithyka', mithika: 'mithyka', mityka: 'mithyka',
    saizon: 'cyzone', cyzon: 'cyzone', cizon: 'cyzone', lebel: "l'bel"
  });
  function normalizeQuery(value) {
    return value.trim().replace(/\s+/g, ' ').replace(/\bl\s+bel\b/gi, "l'bel").replace(/[\p{L}]+/gu, word => {
      const key = word.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
      return aliases[key] || word;
    });
  }
  const plain = value => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  function distance(a, b) {
    const rows = Array.from({ length: a.length + 1 }, (_, i) => [i]);
    for (let j = 0; j <= b.length; j++) rows[0][j] = j;
    for (let i = 1; i <= a.length; i++) for (let j = 1; j <= b.length; j++) {
      rows[i][j] = Math.min(rows[i-1][j]+1, rows[i][j-1]+1, rows[i-1][j-1]+(a[i-1] === b[j-1] ? 0 : 1));
      if (i > 1 && j > 1 && a[i-1] === b[j-2] && a[i-2] === b[j-1]) rows[i][j] = Math.min(rows[i][j], rows[i-2][j-2]+1);
    }
    return rows[a.length][b.length];
  }
  function correctQuery(value, words) {
    return plain(normalizeQuery(value)).replace(/[a-z]+/g, token => {
      if (token.length < 4 || words.includes(token)) return token;
      const matches = words.filter(word => Math.abs(word.length-token.length) <= 1 && word[0] === token[0]).map(word => ({word, d:distance(token,word)})).filter(x => x.d <= (token.length > 7 ? 2 : 1)).sort((a,b) => a.d-b.d);
      return matches.length && (!matches[1] || matches[0].d < matches[1].d) ? matches[0].word : token;
    });
  }
  // Strip conversational introductions; keep product names, descriptors and quantities.
  function productQuery(value) {
    let query=normalizeQuery(value).replace(/[¿?¡!]/g,'').trim();
    query=query.replace(/^(?:hola[, ]+|buenas[, ]+|buenos d[ií]as[, ]+)/i,'');
    const before=query;
    query=query.replace(/^(?:(?:yo )?(?:quiero(?: comprar| ver)?|busco|necesito|estoy buscando(?: comprar)?|me gustar[ií]a(?: comprar| ver)?|quisiera(?: comprar| ver)?|tienes|tienen|tendr[aá]n|buscame|b[uú]scame|mu[eé]strame|me muestras|dame opciones de|puedes mostrarme|puedes buscarme|me puedes mostrar|me puedes buscar)\s+)+/i,'');
    if(query!==before) query=query.replace(/^(?:(?:el|la|los|las|un|una|unos|unas|algun|alg[uú]n)\s+)+/i,'');
    query=query.replace(/[, ]+(?:por favor|porfa|gracias)[. ]*$/i,'').trim();
    return query || normalizeQuery(value).trim();
  }
  function familyQuery(value) {
    return normalizeQuery(value).replace(/\b(perfumes?|colonias?|cremas?|desodorantes?|promopacks?|promos?|packs?|parfums?|eau|de|para|mujer|hombre|ml|frasco|perfume)\b/gi, '').replace(/\b\d+\b/g, '').replace(/\s+/g,' ').trim();
  }
  function groupProducts(products) {
    const groups = { products: [], packs: [], complements: [] };
    const seen = new Set();
    for (const p of products) {
      const key = String(p.id || p.url);
      if (!p.url || seen.has(key)) continue;
      seen.add(key);
      const name = plain(p.title);
      const group = /promopack|promo\b|pack\b|combo|\d\s*x\s*\d/.test(name) ? 'packs' : /crema|desodorante|locion corporal|body|gel de ducha|jabon/.test(name) ? 'complements' : 'products';
      groups[group].push(p);
    }
    return groups;
  }
  function safeProductURL(value, origin) {
    try { const u = new URL(value, origin); return u.origin === origin && u.pathname.startsWith('/products/') ? u.pathname + u.search : null; } catch { return null; }
  }
  function cachedProducts(cache, key, now = Date.now()) {
    const entry = cache.get(key);
    if (entry && entry.expires > now) return entry.products;
    cache.delete(key);
  }
  function matchingPacks(products, query) {
    const tokens = [...new Set(plain(familyQuery(productQuery(query))).match(/[a-z0-9]+/g) || [])].filter(t => t.length > 2);
    if (!tokens.length) return [];
    return products.map((product, index) => {
      const words = plain(normalizeQuery(product.title)).match(/[a-z0-9]+/g) || [];
      let score = 0;
      for (const token of tokens) {
        const costs = words.map(word => {
          if (word === token) return 0;
          if (word.startsWith(token)) return 1;
          if (token.length < 4 || word.length < 4 || word[0] !== token[0] || Math.abs(word.length-token.length) > 1) return Infinity;
          const d = distance(token, word);
          return d <= (token.length > 7 ? 2 : 1) ? 2 + d : Infinity;
        });
        score += Math.min(...costs);
      }
      return { product, score, index };
    }).filter(match => Number.isFinite(match.score))
      .sort((a,b) => Number(a.product.available === false)-Number(b.product.available === false) || a.score-b.score || a.index-b.index)
      .map(match => match.product);
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { normalizeQuery, correctQuery, distance, familyQuery, groupProducts, productQuery, safeProductURL, cachedProducts, matchingPacks };
  if (typeof customElements === 'undefined' || customElements.get('faro-search')) return;

  class FaroSearch extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      this.input = this.querySelector('input[name=q]');
      this.form = this.querySelector('[data-search-form]');
      this.panel = this.querySelector('.faro-search__panel');
      this.list = this.querySelector('[role=listbox]');
      this.notice = this.querySelector('.faro-search__notice');
      this.status = this.querySelector('[data-search-status]');
      this.cache = new Map(); this.catalogCache = new Map(); this.version = 0; this.active = -1;
      const on = (node,event,fn) => node.addEventListener(event,fn,{signal:this.events.signal});
      on(this.input,'input',() => this.schedule());
      on(this.input,'focus',() => { if (this.input.value.trim().length > 1) this.schedule(); });
      on(this.input,'keydown',event => this.onKey(event));
      on(this.form,'submit',event => {
        const query=productQuery(this.input.value);
        if(!query) {event.preventDefault();this.dismiss();return;}
        this.input.value=this.resolvedRaw===this.input.value.trim()?this.resolvedQuery:query;
      });
      on(this,'click',event => {
        const b = event.target.closest('button'); if (!b) return;
        if (b.hasAttribute('data-close')) { this.dismiss(); this.input.focus({preventScroll:true}); this.close(); }
      });
      on(document,'pointerdown',event => { if(!this.contains(event.target)) this.dismiss(); });
      on(this,'keydown',event => { if(event.key==='Escape') { event.preventDefault(); this.dismiss(); this.input.focus({preventScroll:true}); this.close(); } });
      on(this,'focusout',event => { if(event.relatedTarget && !this.contains(event.relatedTarget)) this.dismiss(); });
      // Pin search directly below the persistent header; home brands still scroll away.
      this.bar = this.closest('[data-faro-sticky]'); this.anchor = this.bar.parentElement;
      this.header = document.querySelector('.section-header');
      this.headerSurface = this.header?.querySelector('sticky-header') || this.header;
      const alignSearch = () => {
        this.stickyFrame = null;
        const hasStickyHeader = this.header?.querySelector('sticky-header[data-sticky-type="always"], sticky-header[data-sticky-type="reduce-logo-size"]');
        const offset = hasStickyHeader ? Math.max(0, this.headerSurface.getBoundingClientRect().bottom) : 0;
        if (this.headerOffset !== offset) {
          this.headerOffset = offset;
          document.documentElement.style.setProperty('--faro-header-height', offset+'px');
        }
        this.bar.classList.toggle('is-pinned', this.anchor.getBoundingClientRect().top <= offset + 1);
      };
      const scheduleAlignment = () => {
        if (this.stickyFrame == null) this.stickyFrame = requestAnimationFrame(alignSearch);
      };
      this.resize = new ResizeObserver(() => {
        this.anchor.style.minHeight = this.bar.offsetHeight+'px';
        document.documentElement.style.setProperty('--faro-search-height', this.bar.offsetHeight+'px');
        scheduleAlignment();
      });
      this.resize.observe(this.bar);
      // Scroll tracking also covers short movements and Safari's changing viewport,
      // where a fractional IntersectionObserver threshold can miss the pin boundary.
      const scrollOptions = {passive: true, signal: this.events.signal};
      window.addEventListener('scroll', scheduleAlignment, scrollOptions);
      window.addEventListener('resize', scheduleAlignment, scrollOptions);
      window.visualViewport?.addEventListener('resize', scheduleAlignment, scrollOptions);
      window.visualViewport?.addEventListener('scroll', scheduleAlignment, scrollOptions);
      this.headerResize = new ResizeObserver(scheduleAlignment);
      if (this.headerSurface) this.headerResize.observe(this.headerSurface);
      alignSearch();
      try { sessionStorage.removeItem('faro-advice-v1'); } catch {}

    }
    disconnectedCallback() { this.dismiss(); this.events?.abort(); this.resize?.disconnect(); this.headerResize?.disconnect(); cancelAnimationFrame(this.stickyFrame); this.stickyFrame=null; this.headerOffset=null; this.events=null; }
    open() { this.panel.hidden=false; this.input.setAttribute('aria-expanded','true'); }
    close() { this.panel.hidden=true; this.input.setAttribute('aria-expanded','false'); this.input.removeAttribute('aria-activedescendant'); this.active=-1; }
    dismiss() { ++this.version; clearTimeout(this.timer); this.request?.abort(); this.close(); this.status.textContent=''; }
    schedule() {
      const version=++this.version; clearTimeout(this.timer); this.request?.abort(); this.active=-1; this.input.removeAttribute('aria-activedescendant');
      const raw=this.input.value.trim();
      if(raw.length<2) { this.close(); this.status.textContent=''; this.list.replaceChildren(); this.querySelector('[data-all-results]').hidden=true; return; }
      this.open(); this.list.replaceChildren(); this.querySelector('[data-all-results]').hidden=true; this.notice.textContent='Buscando productos…'; this.status.textContent='Buscando productos…';
      this.timer=setTimeout(()=>this.search(raw,version),260);
    }
    async fetchProducts(query,signal) {
      const key=query.toLowerCase(); const cached=cachedProducts(this.cache,key); if(cached) return cached;
      const url=new URL(this.dataset.searchUrl+'.json',location.origin);
      url.search=new URLSearchParams({q:query,'resources[type]':'product','resources[limit]':'10','resources[options][unavailable_products]':'last'});
      const response=await fetch(url,{signal,headers:{Accept:'application/json'}}); if(!response.ok) throw new Error('search_unavailable');
      const data=await response.json(); const products=(data.resources?.results?.products || []).filter(p=>safeProductURL(p.url,location.origin));
      this.cache.set(key,{products,expires:Date.now()+30000}); if(this.cache.size>40) this.cache.delete(this.cache.keys().next().value); return products;
    }
    async fetchPublishedPacks(signal) {
      const cached=cachedProducts(this.catalogCache,'packs'); if(cached) return cached;
      const root=window.Shopify?.routes?.root || '/';
      const url=new URL(root+'collections/promopacks',location.origin);
      url.searchParams.set('section_id','faro-search-catalog');
      const products=[];
      // Fetch every page; never cache a partial or failed catalog.
      for(let page=1;page<=4;page++) {
        url.searchParams.set('page',String(page));
        const response=await fetch(url,{signal,cache:'no-cache',headers:{Accept:'text/html'}});
        if(!response.ok) throw new Error('pack_catalog_unavailable');
        const html=new DOMParser().parseFromString(await response.text(),'text/html');
        const payload=html.querySelector('[data-faro-search-catalog]');
        if(!payload) throw new Error('pack_catalog_unavailable');
        const data=JSON.parse(payload.textContent);
        if(!Array.isArray(data.products)) throw new Error('pack_catalog_invalid');
        products.push(...data.products.filter(p=>p.available===true && typeof p.title==='string' && safeProductURL(p.url,location.origin)));
        if(data.next===null) {
          this.catalogCache.set('packs',{products,expires:Date.now()+60000}); return products;
        }
        if(data.next!==page+1) throw new Error('pack_catalog_invalid');
      }
      throw new Error('pack_catalog_limit');
    }
    async search(raw,version) {
      this.request=new AbortController(); const signal=this.request.signal; let query=productQuery(raw);
      try {
        const family=familyQuery(query);
        // Both sources settle independently: a missing index entry or a failed feed
        // must not hide matches returned by the other source.
        const catalog=family.length>=3 ? Promise.allSettled([this.fetchPublishedPacks(signal)]) : Promise.resolve([]);
        const primary=await Promise.allSettled([this.fetchProducts(query,signal)]);
        let products=primary[0].status==='fulfilled'?primary[0].value:[];
        if(!products.length && this.dataset.termsUrl) {
          if(!this.words) { const result=await Promise.allSettled([fetch(this.dataset.termsUrl,{signal}).then(r=>r.ok?r.json():[])]); if(result[0].status==='fulfilled') this.words=result[0].value; }
          const corrected=correctQuery(query,this.words || []);
          if(corrected!==plain(query)) { const result=await Promise.allSettled([this.fetchProducts(corrected,signal)]); const matches=result[0].status==='fulfilled'?result[0].value:[]; if(matches.length) {query=corrected; products=matches;} }
        }
        if(version!==this.version) return;
        this.render(products,raw,query);
        if(family.length>=3) {
          const [extra, published]=await Promise.all([
            Promise.allSettled(['promopack '+family,'crema '+family,'desodorante '+family].filter(q=>plain(q)!==plain(query)).map(q=>this.fetchProducts(q,signal))), catalog
          ]);
          if(version!==this.version) return;
          // Never add unrelated generic accessories: every suggested item must match this family.
          const tokens=plain(familyQuery(query)).split(/\s+/).filter(t=>t.length>2);
          const additions=extra.flatMap(r=>r.status==='fulfilled'?r.value:[]).filter(p=>tokens.every(t=>plain(p.title).includes(t)));
          const packs=published[0]?.status==='fulfilled'?matchingPacks(published[0].value,raw):[];
          if(primary[0].status==='rejected' && published[0]?.status==='rejected' && !additions.length) throw new Error('search_unavailable');
          if(this.active<0 && !this.list.contains(document.activeElement)) this.render([...packs,...products,...additions],raw,query);
        } else if(primary[0].status==='rejected') {
          throw new Error('search_unavailable');
        }
      } catch(error) {
        if(error.name==='AbortError'||version!==this.version) return;
        this.notice.textContent='No pudimos cargar las sugerencias. Pulsa «Ver todos los resultados» para continuar.';
        this.updateAll(query); this.status.textContent=this.notice.textContent;
      }
    }
    updateAll(query) { const a=this.querySelector('[data-all-results]'); const u=new URL(this.form.action); u.search=new URLSearchParams({q:query,type:'product','options[prefix]':'last'}); a.href=u.href; a.hidden=false; }
    render(products,raw,query) {
      this.resolvedRaw=raw; this.resolvedQuery=query;
      this.list.replaceChildren(); const groups=groupProducts(products); let index=0;
      for(const [key,label] of [['products','Productos'],['packs','Promopacks de este producto'],['complements','Para complementar']]) {
        if(!groups[key].length) continue;
        const group=document.createElement('li'); group.setAttribute('role','group'); group.setAttribute('aria-label',label);
        const heading=document.createElement('div'); heading.className='faro-search__group-label'; heading.textContent=label; heading.setAttribute('aria-hidden','true'); group.append(heading);
        groups[key].slice(0,key==='products'?3:2).forEach(product=>{
          const href=safeProductURL(product.url,location.origin); if(!href) return;
          const link=document.createElement('a'); link.className='faro-search__result'; link.href=href; link.id=this.list.id+'-'+index++; link.setAttribute('role','option'); link.setAttribute('aria-selected','false'); link.tabIndex=0;
          const img=document.createElement('img'); img.alt=''; img.width=img.height=48;
          const source=product.featured_image?.url || product.image;
          if(source) { try { const u=new URL(source,location.origin); if(u.protocol==='https:') {u.searchParams.set('width','96'); img.src=u.href;} } catch {} }
          const text=document.createElement('span'); const title=document.createElement('strong'); title.textContent=product.title;
          const detail=document.createElement('small'); const amount=Number(product.price);
          detail.textContent=Number.isFinite(amount)?new Intl.NumberFormat('es-CO',{style:'currency',currency:this.dataset.currency||'COP',maximumFractionDigits:0}).format(amount):'';
          if(product.available===false) detail.textContent+=' · Agotado';
          text.append(title,detail); link.append(img,text); group.append(link);
        }); this.list.append(group);
      }
      this.notice.textContent=index ? (plain(query)!==plain(raw)?`Resultados para «${query}»`:'Tu favorito y más opciones para elegir'):'No encontramos coincidencias. Prueba con la marca o una parte del nombre.';
      this.updateAll(query); this.active=-1;
      this.status.textContent=index?`${index} productos sugeridos. Usa las flechas para recorrerlos.`:this.notice.textContent;
    }
    onKey(event) {
      if(this.panel.hidden) return;
      const items=[...this.list.querySelectorAll('[role=option]')];
      if(event.key==='Enter'&&this.active>=0&&items[this.active]) { event.preventDefault(); items[this.active].click(); }
      else if(['ArrowDown','ArrowUp'].includes(event.key)&&items.length) {
        event.preventDefault(); this.active=(this.active+(event.key==='ArrowDown'?1:-1)+items.length)%items.length;
        items.forEach((item,index)=>item.setAttribute('aria-selected',String(index===this.active)));
        this.input.setAttribute('aria-activedescendant',items[this.active].id); items[this.active].scrollIntoView({block:'nearest'});
      }
    }
  }
  customElements.define('faro-search',FaroSearch);
})();
